import {
  Button,
  DatePicker,
  Divider,
  Empty,
  Form,
  Skeleton,
  Typography,
} from 'antd';
import { useEffect, useMemo } from 'react';

import { CustomAlert, LogsGenerator } from '@app/components';
import { useUserHistoryTab } from '@app/components/EmployeeHistoryTab/hooks/useUserHistoryTab';
import {
  DATE_PICKER_FORMAT,
  ERROR,
  ERROR_REQUIRED_FIELD,
  PREFIX,
  rangePickerLocale,
} from '@app/constants';
import { useMemoizeObject } from '@app/hooks';
import type { NSmallGlobalEntities } from '@app/types';
import type { NUsers } from '@app/types/Users';

import '@app/components/EmployeeHistoryTab/styles/UserHistoryTab.desktop.scss';
import '@app/components/EmployeeHistoryTab/styles/UserHistoryTab.tablet.scss';

const { RangePicker } = DatePicker;

export const UserHistoryTab = (data: NUsers.EditUserHistoryProps) => {
  const { user } = data;

  const {
    form,
    submitForm,
    isLoading,
    historyError,
    setHistoryError,
    actions,
    today,
    weekBeforeToday,
    disabledDate,
    value,
    setValue,
    dates,
    setDates,
    getUserActionHistory,
  } = useUserHistoryTab();

  const memoizedUser = useMemoizeObject(user);
  const memoizedToday = useMemoizeObject(today) || null;
  const memoizedWeekBeforeToday = useMemoizeObject(weekBeforeToday) || null;
  const memoizedActions = useMemoizeObject(actions);

  useEffect(() => {
    const Date: NSmallGlobalEntities.RangeValue = [
      memoizedWeekBeforeToday,
      memoizedToday,
    ];
    const values = {
      Date,
    };

    submitForm(values, memoizedUser?.id, getUserActionHistory);
    // Добавление в список зависимостей submitForm приводит к циклическому вызову GetUserActionHistory
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memoizedToday,
    memoizedUser,
    memoizedWeekBeforeToday,
    getUserActionHistory,
  ]);

  const logsContent = useMemo(() => {
    const handledMemoizedActions = memoizedActions || [];
    const hasData = handledMemoizedActions.length;

    if (isLoading) return <Skeleton />;

    if (!hasData) return <Empty />;

    return (
      <LogsGenerator
        actions={handledMemoizedActions}
        isEmployee={user?.isEmployee}
      />
    );
  }, [user, isLoading, memoizedActions]);

  return (
    <div className={`${PREFIX}users-history-tab`}>
      <div className={'dates' + 'bc-form'}>
        <Form
          form={form}
          style={{ maxWidth: 800, width: '100%' }}
          onFinish={(values) =>
            submitForm(values, user?.id, getUserActionHistory)
          }
          initialValues={{
            Date: [weekBeforeToday, today],
          }}
        >
          <div className="space">
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="period-title"
              >
                Период:
              </Typography.Text>

              <Form.Item
                name="Date"
                className="mg-0"
                rules={[
                  {
                    required: true,
                    message: ERROR_REQUIRED_FIELD,
                  },
                ]}
              >
                <RangePicker
                  locale={rangePickerLocale}
                  format={DATE_PICKER_FORMAT}
                  size="large"
                  value={dates || value}
                  disabledDate={disabledDate}
                  onCalendarChange={(val) => {
                    setDates(val);
                  }}
                  onChange={(val) => {
                    setValue(val);
                  }}
                />
              </Form.Item>
            </div>

            {historyError && (
              <CustomAlert
                errorType={ERROR}
                serverError={historyError}
                setServerError={setHistoryError}
              />
            )}

            <Button
              type="primary"
              htmlType="submit"
              className="save-button"
              size="large"
              loading={isLoading}
              data-testid="show-button"
            >
              Показать
            </Button>
          </div>
        </Form>
      </div>

      <Divider />

      {logsContent}
    </div>
  );
};
